import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  Filter,
  HelpCircle,
  MessageSquare,
  Paperclip,
  RefreshCcw,
  Send,
  X,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';

interface ConversationFile {
  name: string;
  url: string;
}

interface ConversationMessage {
  id: string;
  type: 'request-info' | 'response' | 'rejection' | 'resent';
  sender: 'core' | 'skewbworks';
  timestamp: string;
  message: string;
  createdBy?: string | null;
  eventType?: string;
  files?: ConversationFile[];
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  'work_order.new': 'Received',
  'work_order.accepted': 'Accepted',
  'work_order.rejected': 'Rejected',
  'work_order.more_info_requested': 'Info Requested',
  'work_order.more_info_received': 'Info Received',
  'work_order.scheduled': 'Scheduled',
  'work_order.unscheduled': 'Unscheduled',
  'work_order.duration_challenge': 'Duration Challenge',
  'work_order.duration_challenge_approved': 'DCR Response',
  'work_order.engineer_en_route': 'En Route',
  'work_order.engineer_on_site': 'On Site',
  'work_order.aborted': 'Aborted',
  'work_order.engineering_complete': 'Eng. Complete',
  'work_order.dfe_submitted': 'DFE Submitted',
  'work_order.dfe_more_info_requested': 'DFE Info Requested',
  'work_order.dfe_approved': 'DFE Approved',
  'work_order.dfe_withdrawn': 'DFE Withdrawn',
  'work_order.dfe_resubmitted': 'DFE Resubmitted',
  'work_order.completed': 'Completed',
  //
  'work_order.update': 'Update',
  'work_order.validated': 'Validated',
  'work_order.validation_requested': 'Validation Requested',
};

interface ConversationThreadProps {
  conversationThread: ConversationMessage[];
  jobStatus: string;
  showResponseForm: boolean;
  setShowResponseForm: (show: boolean) => void;
  responseMessage: string;
  setResponseMessage: (message: string) => void;
  onSendResponse: () => void;
}

export function ConversationThread({
  conversationThread,
  jobStatus,
  showResponseForm,
  setShowResponseForm,
  responseMessage,
  setResponseMessage,
  onSendResponse,
}: ConversationThreadProps) {
  const [senderFilter, setSenderFilter] = useState<'all' | 'skewbworks' | 'core'>('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const eventTypesInThread = useMemo(() => {
    const types = new Set<string>();
    for (const m of conversationThread) {
      if (m.eventType) types.add(m.eventType);
    }
    return Array.from(types).sort();
  }, [conversationThread]);

  const filtered = useMemo(() => {
    return conversationThread.filter((msg) => {
      if (senderFilter !== 'all' && msg.sender !== senderFilter) return false;
      if (eventTypeFilter !== 'all' && msg.eventType !== eventTypeFilter) return false;
      if (dateFrom) {
        const msgDate = msg.timestamp.split('T')[0];
        if (msgDate < dateFrom) return false;
      }
      if (dateTo) {
        const msgDate = msg.timestamp.split('T')[0];
        if (msgDate > dateTo) return false;
      }
      return true;
    });
  }, [conversationThread, senderFilter, eventTypeFilter, dateFrom, dateTo]);

  const activeFilterCount =
    (senderFilter !== 'all' ? 1 : 0) +
    (eventTypeFilter !== 'all' ? 1 : 0) +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0);

  const clearFilters = () => {
    setSenderFilter('all');
    setEventTypeFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Communication History</h3>
              <p className="text-xs text-muted-foreground">
                {filtered.length} of {conversationThread.length} message
                {conversationThread.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'border-[#E84A2B] text-[#E84A2B]' : ''}
            >
              <Filter className="w-4 h-4 mr-1.5" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-1.5 bg-[#E84A2B] text-white border-0 text-[10px] h-5 w-5 p-0 flex items-center justify-center rounded-full">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            {jobStatus === 'more-info-requested' && (
              <Button
                variant="outline"
                size="sm"
                className="border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-950"
                onClick={() => setShowResponseForm(!showResponseForm)}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Response
              </Button>
            )}
            {jobStatus === 'rejected' && (
              <Button
                variant="outline"
                size="sm"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                onClick={() => setShowResponseForm(!showResponseForm)}
              >
                <RefreshCcw className="w-4 h-4 mr-2" />
                Re-send with Info
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mb-4 p-4 bg-muted/30 rounded-lg border border-border space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Filter Messages</span>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs h-7">
                  <X className="w-3 h-3 mr-1" />
                  Clear all
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Source</label>
                <Select value={senderFilter} onValueChange={(v) => setSenderFilter(v as any)}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="skewbworks">SkewbWorks</SelectItem>
                    <SelectItem value="core">CORE Cockpit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Event Type</label>
                <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    {eventTypesInThread.map((et) => (
                      <SelectItem key={et} value={et}>
                        {EVENT_TYPE_LABELS[et] ?? et.replace('work_order.', '')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">From Date</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">To Date</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>
        )}

        {showResponseForm && (
          <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
            <textarea
              className="w-full min-h-[120px] p-3 border border-border rounded-md bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-[#E84A2B]"
              placeholder={
                jobStatus === 'rejected'
                  ? 'Describe the additional information you are providing...'
                  : 'Provide the requested information...'
              }
              value={responseMessage}
              onChange={(e) => setResponseMessage(e.target.value)}
            />
            <div className="flex items-center justify-between mt-3">
              <Button variant="outline" size="sm">
                <Paperclip className="w-4 h-4 mr-2" />
                Attach Files
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowResponseForm(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
                  onClick={onSendResponse}
                  disabled={!responseMessage.trim()}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </Button>
              </div>
            </div>
          </div>
        )}

        {filtered.length === 0 && conversationThread.length > 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No messages match your filters</p>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="mt-2 text-xs">
              Clear filters
            </Button>
          </div>
        )}

        <div className="space-y-4">
          {filtered.map((message: ConversationMessage, index: number) => {
            const isFromCore = message.sender === 'core';
            const isRejection = message.type === 'rejection';
            const isResent = message.type === 'resent';
            const isResponse = message.type === 'response';
            const isRequest = message.type === 'request-info';

            return (
              <div key={message.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: isFromCore
                        ? isResponse
                          ? '#dcfce7'
                          : '#dbeafe'
                        : isResent
                          ? '#dbeafe'
                          : isRejection
                            ? '#fee2e2'
                            : '#fef3c7',
                      color: isFromCore
                        ? isResponse
                          ? '#16a34a'
                          : '#2563eb'
                        : isResent
                          ? '#2563eb'
                          : isRejection
                            ? '#dc2626'
                            : '#d97706',
                    }}
                  >
                    {isRequest && <HelpCircle className="w-5 h-5" />}
                    {isResponse && <MessageSquare className="w-5 h-5" />}
                    {isRejection && <XCircle className="w-5 h-5" />}
                    {isResent && <RefreshCcw className="w-5 h-5" />}
                  </div>
                  {index < filtered.length - 1 && (
                    <div className="w-0.5 flex-1 min-h-[40px] bg-border mt-2" />
                  )}
                </div>

                <div className="flex-1 pb-6">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-foreground text-sm">
                        {isFromCore ? 'CORE Cockpit' : 'SkewbWorks'}
                        {message.createdBy && (
                          <span className="font-normal text-muted-foreground">
                            {' '}
                            — {message.createdBy}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(message.timestamp).toLocaleString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        isRequest
                          ? 'bg-amber-50 text-amber-700 border-amber-600 dark:bg-amber-950/30'
                          : isResponse
                            ? 'bg-green-50 text-green-700 border-green-600 dark:bg-green-950/30'
                            : isRejection
                              ? 'bg-red-50 text-red-700 border-red-600 dark:bg-red-950/30'
                              : 'bg-blue-50 text-blue-700 border-blue-600 dark:bg-blue-950/30'
                      }
                    >
                      {message.eventType
                        ? (EVENT_TYPE_LABELS[message.eventType] ??
                          message.eventType.replace('work_order.', ''))
                        : isRequest
                          ? 'Info Requested'
                          : isResponse
                            ? 'Response'
                            : isRejection
                              ? 'Rejected'
                              : 'Update'}
                    </Badge>
                  </div>

                  <div className="bg-muted/50 rounded-lg p-4 border border-border">
                    <p className="text-sm text-foreground whitespace-pre-wrap">{message.message}</p>

                    {message.files && message.files.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border space-y-2">
                        <div className="text-xs text-muted-foreground font-medium mb-2">
                          Attachments:
                        </div>
                        {message.files.map((file: ConversationFile, fileIdx: number) => (
                          <div key={fileIdx} className="flex items-center gap-2 text-sm">
                            <Paperclip className="w-4 h-4 text-muted-foreground" />
                            <a
                              href={file.url}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {file.name}
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
